import { captureException } from '@sentry/browser';
import { api } from 'controllers/network/apiClient';
import useAuth from 'hooks/useAuth';
import { MockedEntitlements } from 'providers/entitlements/fixtures';
import { QueryFunctionContext, useQuery } from 'react-query';
import { EntitlementKey, EntitlementKeys, EntitlementsResponse } from './types';

export const ENTITLEMENTS_API_URL = '/v2/shops/entitlements/';
const ENTITLEMENTS_QUERY_KEY = 'entitlements';

export const checkEntitlementKeyMismatch = (
  apiKeys: string[],
  hardcodedKeys: EntitlementKey[],
) => {
  const missingInHardcoded = apiKeys.filter(
    (key) => !hardcodedKeys.includes(key as EntitlementKey),
  );
  const missingInAPI = hardcodedKeys.filter((key) => !apiKeys.includes(key));

  if (process.env.NODE_ENV === 'development') {
    if (missingInHardcoded.length > 0) {
      console.warn(
        '[Entitlements] Keys found in API but missing from hardcoded list:',
        missingInHardcoded,
      );
    }
    if (missingInAPI.length > 0) {
      console.warn(
        '[Entitlements] Keys in hardcoded list but missing from API:',
        missingInAPI,
      );
    }
  }

  return {
    missingInHardcoded,
    missingInAPI,
  };
};

const getEntitlements = async ({
  queryKey,
}: QueryFunctionContext<
  ReturnType<typeof EntitlementsQueryKeys['getEntitlements']>
>) => {
  const [, { shopId }] = queryKey;

  try {
    const response = await api.get<EntitlementsResponse>(ENTITLEMENTS_API_URL, {
      timeout: 5000,
    });

    // Check for key mismatches in development
    checkEntitlementKeyMismatch(
      Object.keys(response.entitlements),
      Object.values(EntitlementKeys),
    );

    return response;
  } catch (error) {
    captureException(error, { extra: { shopId } });

    // Return mocked data as a fallback
    return Promise.resolve({
      entitlements: MockedEntitlements,
      merchant_id: shopId,
    });
  }
};

const EntitlementsQueryKeys = {
  getEntitlements: (shopId: number) =>
    [ENTITLEMENTS_QUERY_KEY, { shopId }] as const,
};

/** Do not call this hook directly! Only interact with Entitlements via useEntitlements() */
export const useGetEntitlements = () => {
  const { isLoggedIn, getShopId } = useAuth();

  return useQuery({
    queryKey: EntitlementsQueryKeys.getEntitlements(getShopId()),
    queryFn: getEntitlements,

    enabled: isLoggedIn,
  });
};
